import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import './AdminDashboard.css';
import ImageGalleryPicker from '../../components/admin/ImageGalleryPicker';

// Image extensions that should render the gallery picker
const IMAGE_FIELD_EXTENSIONS = new Set(['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg']);

// Detect if a field value is (or should be) an image field.
// Matches: /images/... paths with image extensions, OR empty string for fields
// whose key name suggests an image (handled by caller via fieldName check).
function isImagePath(val) {
    if (typeof val !== 'string') return false;
    if (!val.startsWith('/images/')) return false;
    const ext = val.substring(val.lastIndexOf('.')).toLowerCase();
    return IMAGE_FIELD_EXTENSIONS.has(ext);
}

// Detect if a field KEY name suggests it holds an image
const IMAGE_KEY_NAMES = new Set(['image', 'icon', 'logo', 'backgroundImage', 'src', 'photo', 'thumbnail']);
function isImageFieldName(fieldName) {
    return IMAGE_KEY_NAMES.has(fieldName);
}

function AdminDashboard() {
    const [content, setContent] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [activeSection, setActiveSection] = useState('block1');
    const [searchTerm, setSearchTerm] = useState('');
    const [unsavedChanges, setUnsavedChanges] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        // Check if authenticated
        const token = localStorage.getItem('token');
        if (!token) {
            navigate('/admin/login');
            return;
        }

        // Fetch content
        fetchContent();
    }, [navigate]);

    const fetchContent = async () => {
        try {
            const apiUrl = process.env.REACT_APP_API_URL || '';
            const response = await fetch(`${apiUrl}/api/content`);

            if (response.ok) {
                const data = await response.json();
                setContent(data);
            } else {
                setError('Failed to load content');
            }
        } catch (err) {
            setError('Network error. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        setError('');
        setSuccess('');

        const token = localStorage.getItem('token');

        try {
            const apiUrl = process.env.REACT_APP_API_URL || '';
            const response = await fetch(`${apiUrl}/api/content`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(content)
            });

            if (response.status === 401) {
                localStorage.removeItem('token');
                navigate('/admin/login');
                return;
            }

            if (response.ok) {
                setSuccess('✓ Content saved successfully!');
                setUnsavedChanges(false);
                setTimeout(() => setSuccess(''), 3000);
            } else {
                const data = await response.json();
                setError(data.error || 'Failed to save content');
            }
        } catch (err) {
            setError('Network error. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    const handleLogout = () => {
        localStorage.removeItem('token');
        navigate('/admin/login');
    };

    const updateField = (section, field, value) => {
        setUnsavedChanges(true);
        setContent(prev => ({
            ...prev,
            [section]: {
                ...prev[section],
                [field]: value
            }
        }));
    };

    const updateNestedField = (section, path, value) => {
        setUnsavedChanges(true);
        setContent(prev => {
            const sectionData = { ...prev[section] };
            let current = sectionData;
            for (let i = 0; i < path.length - 1; i++) {
                current[path[i]] = Array.isArray(current[path[i]])
                    ? [...current[path[i]]]
                    : { ...current[path[i]] };
                current = current[path[i]];
            }
            current[path[path.length - 1]] = value;
            return { ...prev, [section]: sectionData };
        });
    };

    const updateArrayItem = (section, field, index, itemField, value) => {
        setUnsavedChanges(true);
        setContent(prev => {
            const newArray = [...(prev[section][field] || [])];
            newArray[index] = {
                ...newArray[index],
                [itemField]: value
            };
            return {
                ...prev,
                [section]: {
                    ...prev[section],
                    [field]: newArray
                }
            };
        });
    };

    const addArrayItem = (section, field, template) => {
        setUnsavedChanges(true);
        setContent(prev => ({
            ...prev,
            [section]: {
                ...prev[section],
                [field]: [...(prev[section][field] || []), template]
            }
        }));
    };

    const removeArrayItem = (section, field, index) => {
        setUnsavedChanges(true);
        setContent(prev => {
            const newArray = [...(prev[section][field] || [])];
            newArray.splice(index, 1);
            return {
                ...prev,
                [section]: {
                    ...prev[section],
                    [field]: newArray
                }
            };
        });
    };

    const sections = content ? Object.keys(content).filter(key =>
        key.toLowerCase().includes(searchTerm.toLowerCase())
    ) : [];

    const renderField = (section, field, value, path = []) => {
        const fullPath = [...path, field].join('.');
        const fieldPath = [...path, field];

        if (Array.isArray(value)) {
            return (
                <div key={fullPath} className="field-group array-field">
                    <label className="field-label">
                        {field.replace(/([A-Z])/g, ' $1').trim()}
                        <span className="field-type">Array ({value.length} items)</span>
                    </label>

                    {value.map((item, index) => (
                        <div key={index} className="array-item">
                            <div className="array-item-header">
                                <span className="array-item-number">Item {index + 1}</span>
                                <button
                                    onClick={() => removeArrayItem(section, field, index)}
                                    className="remove-item-btn"
                                    type="button"
                                >
                                    Remove
                                </button>
                            </div>

                            {typeof item === 'object' && item !== null ? (
                                <div className="nested-fields">
                                    {Object.entries(item).map(([itemField, itemValue]) => {
                                        // BUG-8 fix: detect image fields by path OR by field name (for empty/new fields)
                                        const itemIsImage = isImagePath(itemValue) || (
                                            (itemValue === '' || itemValue === null || itemValue === undefined)
                                            && isImageFieldName(itemField)
                                        );
                                        return (
                                            <div key={itemField} className="field-group">
                                                <label className="field-label-small">
                                                    {itemField.replace(/([A-Z])/g, ' $1').trim()}
                                                </label>
                                                {itemIsImage ? (
                                                    <>
                                                        {itemValue && (
                                                            <div className="image-preview">
                                                                {/* BUG Sally#13 fix: meaningful alt text */}
                                                                <img src={itemValue} alt={`Current ${itemField}`} />
                                                            </div>
                                                        )}
                                                        <ImageGalleryPicker
                                                            currentValue={itemValue ?? ''}
                                                            fieldLabel={itemField}
                                                            onSelect={(selectedPath) => updateArrayItem(section, field, index, itemField, selectedPath)}
                                                        />
                                                    </>
                                                ) : typeof itemValue === 'string' && itemValue.length > 100 ? (
                                                    <textarea
                                                        value={itemValue ?? ''}
                                                        onChange={(e) => updateArrayItem(section, field, index, itemField, e.target.value)}
                                                        rows={3}
                                                        className="field-input"
                                                    />
                                                ) : (
                                                    <input
                                                        type="text"
                                                        value={itemValue ?? ''}
                                                        onChange={(e) => updateArrayItem(section, field, index, itemField, e.target.value)}
                                                        className="field-input"
                                                    />
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : (
                                <input
                                    type="text"
                                    value={item ?? ''}
                                    onChange={(e) => {
                                        const newArray = [...value];
                                        newArray[index] = e.target.value;
                                        if (path.length === 0) {
                                            updateField(section, field, newArray);
                                        } else {
                                            updateNestedField(section, fieldPath, newArray);
                                        }
                                    }}
                                    className="field-input"
                                />
                            )}
                        </div>
                    ))}

                    <button
                        onClick={() => {
                            const template = value.length > 0 && typeof value[0] === 'object'
                                ? Object.keys(value[0]).reduce((acc, key) => ({ ...acc, [key]: '' }), {})
                                : '';
                            addArrayItem(section, field, template);
                        }}
                        className="add-item-btn"
                        type="button"
                    >
                        + Add Item
                    </button>
                </div>
            );
        }

        if (typeof value === 'object' && value !== null) {
            return (
                <div key={fullPath} className="field-group nested-object">
                    <label className="field-label">
                        {field.replace(/([A-Z])/g, ' $1').trim()}
                        <span className="field-type">Object</span>
                    </label>
                    <div className="nested-fields">
                        {Object.entries(value).map(([nestedField, nestedValue]) =>
                            renderField(section, nestedField, nestedValue, fieldPath)
                        )}
                    </div>
                </div>
            );
        }

        const strValue = value ?? '';
        // BUG-8 / Winston#3 fix: detect image fields by path (with extension check) OR by field name
        const isImageUrl = isImagePath(strValue) || (
            (strValue === '' || strValue === null)
            && isImageFieldName(field)
        );
        const isLongText = !isImageUrl && typeof strValue === 'string' && strValue.length > 100;

        const handleChange = (newValue) => {
            if (path.length === 0) {
                updateField(section, field, newValue);
            } else {
                updateNestedField(section, fieldPath, newValue);
            }
        };

        return (
            <div key={fullPath} className="field-group">
                <label className="field-label">
                    {field.replace(/([A-Z])/g, ' $1').trim()}
                    {isImageUrl && <span className="field-type">Image</span>}
                </label>

                {isImageUrl && strValue && (
                    <div className="image-preview">
                        {/* BUG Sally#13 fix: meaningful alt text */}
                        <img src={strValue} alt={`Current ${field}`} />
                    </div>
                )}

                {isImageUrl ? (
                    <ImageGalleryPicker
                        currentValue={strValue}
                        fieldLabel={field}
                        onSelect={handleChange}
                    />
                ) : isLongText ? (
                    <textarea
                        value={strValue}
                        onChange={(e) => handleChange(e.target.value)}
                        rows={4}
                        className="field-input"
                    />
                ) : (
                    <input
                        type="text"
                        value={strValue}
                        onChange={(e) => handleChange(e.target.value)}
                        className="field-input"
                    />
                )}
            </div>
        );
    };

    if (loading) {
        return (
            <div className="admin-dashboard">
                <div className="loading-screen">
                    <div className="spinner"></div>
                    <p>Loading content...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="admin-dashboard">

            {/* Header */}
            <header className="dashboard-header">
                <div className="header-left">
                    <h1>Content Editor</h1>
                    <p className="header-subtitle">Edit your website content</p>
                </div>

                <div className="header-right">
                    <Link to="/admin/messages" className="header-link">
                        📬 Messages
                    </Link>
                    <Link to="/admin/media" className="header-link">
                        📁 Media Manager
                    </Link>
                    <button onClick={handleLogout} className="logout-btn">
                        Logout
                    </button>
                </div>
            </header>

            {/* Notifications */}
            {success && (
                <div className="notification success-notification">
                    {success}
                </div>
            )}

            {error && (
                <div className="notification error-notification">
                    {error}
                </div>
            )}

            {/* Main Content */}
            <div className="dashboard-content">

                {/* Sidebar */}
                <aside className="dashboard-sidebar">
                    <div className="sidebar-search">
                        <input
                            type="text"
                            placeholder="Search sections..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="search-input"
                        />
                    </div>

                    <nav className="sidebar-nav">
                        <div className="nav-section">
                            <h3 className="nav-section-title">Content Blocks</h3>
                            {sections.filter(s => s.startsWith('block')).map(section => (
                                <button
                                    key={section}
                                    onClick={() => setActiveSection(section)}
                                    className={`nav-item ${activeSection === section ? 'active' : ''}`}
                                >
                                    <span className="nav-icon">📄</span>
                                    {section.replace('block', 'Block ')}
                                </button>
                            ))}
                        </div>

                        <div className="nav-section">
                            <h3 className="nav-section-title">Special Sections</h3>
                            {sections.filter(s => !s.startsWith('block')).map(section => (
                                <button
                                    key={section}
                                    onClick={() => setActiveSection(section)}
                                    className={`nav-item ${activeSection === section ? 'active' : ''}`}
                                >
                                    <span className="nav-icon">
                                        {section === 'navbar' ? '🧭' :
                                            section === 'footer' ? '📍' :
                                                section === 'pageHero' ? '🎬' :
                                                    section.includes('job') ? '💼' :
                                                        section.includes('join') ? '👥' : '📝'}
                                    </span>
                                    {section.replace(/([A-Z])/g, ' $1').trim()}
                                </button>
                            ))}
                        </div>
                    </nav>
                </aside>

                {/* Editor */}
                <main className="dashboard-main">
                    <div className="editor-header">
                        <h2 className="editor-title">
                            {activeSection.replace(/([A-Z])/g, ' $1').trim()}
                        </h2>
                        <div className="editor-header-actions">
                            {unsavedChanges && (
                                <span className="unsaved-indicator">● Unsaved changes</span>
                            )}
                            <button
                                onClick={handleSave}
                                disabled={saving}
                                className={`save-btn${unsavedChanges ? ' save-btn-highlight' : ''}`}
                            >
                                {saving ? 'Saving...' : '💾 Save Changes'}
                            </button>
                        </div>
                    </div>

                    <div className="editor-content">
                        {content && content[activeSection] && (
                            <form onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
                                {Object.entries(content[activeSection]).map(([field, value]) =>
                                    renderField(activeSection, field, value)
                                )}
                            </form>
                        )}
                    </div>
                </main>

            </div>
        </div>
    );
}

export default AdminDashboard;
